import { removeDuplicates } from '@thecompaniesapi/array-helpers';
import { walkObject, removeNullUndefinedEmpty } from '@thecompaniesapi/object-helpers';
import { removeAccents, camelCase, removeEmojis } from '@thecompaniesapi/string-helpers';

function cleanName(params) {
  if (!params.name) {
    return "";
  }
  params.name = params.name.toLowerCase();
  if (!params.disableFormatName) {
    params.name = formatName({ name: params.name });
  }
  params.name = removeAccents(params.name);
  params.name = params.name.replace(/ *\([^)]*\) */g, "");
  params.name = params.name.replace(/-|_|\./g, " ");
  params.name = params.name.replace(/\s\s+/g, " ");
  return params.name.trim();
}
function extractSocialNetworkStats(params) {
  const stats = {};
  if (params.string) {
    const regex = /([\d,.]+)(k|m)?\s+(followers|following|likes|posts|talking about|were here)/gi;
    const abbreviationMapping = {
      b: 1e9,
      k: 1e3,
      m: 1e6
    };
    const matches = params.string.matchAll(regex);
    for (const match of matches) {
      let value = Number.parseFloat(match[1].replace(/,/g, ""));
      const key = camelCase(match[3]);
      const abbreviation = match[2]?.toLowerCase();
      if (abbreviationMapping[abbreviation]) {
        value *= abbreviationMapping[abbreviation];
      }
      value = Math.round(value);
      stats[key] = value;
    }
    for (const key in stats) {
      if (!stats[key]) {
        delete stats[key];
      }
    }
  }
  return stats;
}
function extractStockExchangeAndSymbol(params) {
  let exchange = null;
  let symbol = null;
  if (params.string) {
    const regex = /\((\w+):(\w+)(?:\s|\))/;
    const matches = regex.exec(params.string);
    if (matches && matches.length >= 3) {
      exchange = matches[2].toUpperCase();
      symbol = matches[1].toUpperCase();
    }
  }
  return {
    exchange,
    symbol
  };
}
function extractValueAndYear(params) {
  let value = params.string.split("(")[0]?.trim()?.replace(/,/g, "");
  let year = Number.parseInt(params.string.split("(")[1]?.match(/\d{4}/)?.[0] || "");
  if (value && !value.match(/\d/)) {
    value = null;
  }
  if (params.parseIntValue && typeof value === "string") {
    const valueNumber = Number.parseFloat(value.replace(/[^-?\d.]/g, ""));
    if (value.includes(" million")) {
      value = valueNumber * 1e6;
    } else if (value.includes(" billion")) {
      value = valueNumber * 1e9;
    } else if (value.includes(" trillion")) {
      value = valueNumber * 1e12;
    } else {
      value = Number.parseInt(value);
    }
    if (Number.isNaN(value)) {
      value = null;
    }
  }
  if (Number.isNaN(year) || !value) {
    year = null;
  }
  return {
    value,
    year
  };
}
function extractYearFoundedAttributes(params) {
  const attributes = {
    yearFounded: null,
    yearFoundedDate: null,
    yearFoundedPlace: null
  };
  if (params.string) {
    attributes.yearFounded = Number.parseInt(params.string.match(/\d{4}/)?.[0] || "");
    if (Number.isNaN(attributes.yearFounded)) {
      attributes.yearFounded = null;
    } else {
      attributes.yearFoundedDate = params.string.split(`${attributes.yearFounded}`)?.[0]?.trim() || null;
      if (attributes.yearFoundedDate) {
        attributes.yearFoundedDate += ` ${attributes.yearFounded}`;
      }
      attributes.yearFoundedPlace = params.string.replace(attributes.yearFoundedDate || "", "").replace(`${attributes.yearFounded}`, "").trim();
      if (!attributes.yearFoundedPlace || attributes.yearFoundedPlace.includes("...")) {
        attributes.yearFoundedPlace = null;
      } else if (attributes.yearFoundedPlace.startsWith(", ")) {
        attributes.yearFoundedPlace = attributes.yearFoundedPlace.replace(", ", "");
      }
    }
  }
  return attributes;
}
function formatName(params) {
  let name = params.name || "";
  name = removeEmojis(name);
  name = name.replace(/,?\s*(a\.ş|a\.p\.c|ag|apc|b\.v|co|co\.ltd|corp|gmbh|gmbh & co|inc|incorporated|kft|kg|limited|llc|l\.l\.c|llp|lp|ltc|ltd|nv|p\.a|plc|private limited|pte|pte ltd|pty ltc|pvt|pvt ltd|sas|s\.a\.s|sarl|s\.a\.r\.l|scs|sdn bhd|®|©)?(\.|\s)?$/i, "");
  name = name.trim();
  return name;
}
function generateNameAlts(params) {
  let companyNames = [];
  if (params.elasticCompany.about?.name) {
    companyNames.push(
      cleanName({
        disableFormatName: false,
        name: params.elasticCompany.about.name
      })
    );
    companyNames.push(
      cleanName({
        disableFormatName: true,
        name: params.elasticCompany.about.name
      })
    );
  }
  if (params.elasticCompany.domain?.domainName) {
    companyNames.push(
      cleanName({ name: params.elasticCompany.domain.domainName })
    );
  }
  if (params.elasticCompany.socials?.linkedin?.id) {
    companyNames.push(cleanName({ name: params.elasticCompany.socials?.linkedin?.id }));
  }
  if (params.additionalNames) {
    for (const name of params.additionalNames) {
      if (name) {
        companyNames.push(
          cleanName({
            disableFormatName: false,
            name
          })
        );
        companyNames.push(
          cleanName({
            disableFormatName: true,
            name
          })
        );
      }
    }
  }
  const forbiddenCompanyNames = ["linkedin"];
  companyNames = companyNames.filter((companyName) => {
    return !forbiddenCompanyNames.some((forbiddenCompanyName) => {
      return companyName.includes(forbiddenCompanyName);
    }) || params.elasticCompany.domain?.domain === "linkedin.com";
  }).reduce((accumulator, companyName) => {
    if (companyName.includes(" ")) {
      return [
        ...accumulator,
        companyName,
        companyName.replace(/\s/g, "")
      ];
    } else {
      return [...accumulator, companyName];
    }
  }, []);
  companyNames = removeDuplicates(companyNames);
  return companyNames;
}
function removeEmptyCategories(company) {
  for (const companyKey in company) {
    const categoryKey = companyKey;
    if (Object.keys(company[categoryKey] || {}).length === 0) {
      delete company[categoryKey];
    }
  }
  return company;
}
function companyV2ToElasticSource(company, callback) {
  return walkObject(
    company,
    (key, value, path) => {
      if (key.endsWith("geopoint")) {
        return {
          key,
          value: {
            lat: Number(value.lat.toFixed(6)),
            lon: Number(value.lon.toFixed(6))
          }
        };
      }
      if (callback) {
        return callback(key, value, path);
      }
      return {
        key,
        value
      };
    }
  );
}
function elasticResponseToCompanyV2(company, callback) {
  return walkObject(
    company,
    (key, value, path) => {
      if (callback) {
        callback(key, value, path);
      }
      return {
        key,
        value
      };
    }
  );
}
function castCompany(company, castTo, callback) {
  if (castTo === "companyV2") {
    return elasticResponseToCompanyV2(company, callback);
  } else if (castTo === "elastic") {
    return companyV2ToElasticSource(company, callback);
  }
  return company;
}
function getScore(company) {
  let score = 0;
  if (company?.about?.businessType) {
    score += 1;
  }
  if (company?.about?.totalEmployeesExact) {
    score += 3;
  }
  if (company?.about?.yearFounded) {
    score += 1;
  }
  if (company?.about?.yearFoundedPlace) {
    score += 1;
  }
  if (company?.about?.industries?.length) {
    score += 3;
  }
  if (company?.about?.name) {
    score += 1;
  }
  if (company?.about?.nameAlts?.length) {
    score += 1;
  }
  if (company?.about?.languages?.length) {
    score += 2;
  }
  if (company?.about?.industry) {
    score += 1;
  }
  if (company?.urls?.website) {
    score += 1;
  }
  if (company?.about?.totalEmployees) {
    switch (company?.about?.totalEmployees) {
      case "1-10":
        score += 1;
        break;
      case "10-50":
        score += 2;
        break;
      case "50-200":
        score += 3;
        break;
      case "200-500":
        score += 4;
        break;
      case "500-1k":
        score += 5;
        break;
      case "1k-5k":
        score += 6;
        break;
      case "5k-10k":
        score += 7;
        break;
      case "over-10k":
        score += 8;
        break;
    }
  }
  if (company?.about?.nameAlts?.length) {
    score += 1;
  }
  if (company?.assets?.colorPrimary) {
    score += 1;
  }
  if (company?.assets?.cover) {
    score += 3;
  }
  if (company?.assets?.logoSquare) {
    score += 5;
  }
  if (company?.companies?.acquisitions?.length) {
    score += 5;
  }
  if (company?.companies?.subsidiaries?.length) {
    score += 5;
  }
  if (company?.companies?.parent) {
    score += 5;
  }
  if (company?.descriptions?.primary) {
    score += 1;
  }
  if (company?.descriptions?.website) {
    score += 1;
  }
  if (company?.descriptions?.knowledgeGraph) {
    score += 1;
  }
  if (company?.descriptions?.wikipedia) {
    score += 1;
  }
  if (company?.finances?.revenue) {
    switch (company?.finances?.revenue) {
      case "under-1m":
        score += 1;
        break;
      case "1m-10m":
        score += 2;
        break;
      case "10m-50m":
        score += 3;
        break;
      case "50m-100m":
        score += 4;
        break;
      case "100m-200m":
        score += 5;
        break;
      case "200m-1b":
        score += 6;
        break;
      case "over-1b":
        score += 10;
        break;
    }
  }
  if (company?.finances?.stockExchange) {
    score += 2;
  }
  if (company?.finances?.stockSymbol) {
    score += 2;
  }
  if (company?.codes?.naics?.length) {
    score += 1;
  }
  if (company?.codes?.sic?.length) {
    score += 1;
  }
  if (company?.locations?.headquarters?.address) {
    score += 5;
  }
  if (company.locations?.headquarters?.city) {
    score += 2;
  }
  if (company.locations?.headquarters?.country) {
    score += 2;
  }
  if (company.locations?.headquarters?.county) {
    score += 2;
  }
  if (company?.locations?.headquarters?.state) {
    score += 2;
  }
  if (company?.analytics?.monthlyVisitors) {
    switch (company?.analytics?.monthlyVisitors) {
      case "under-10k":
        score += 1;
        break;
      case "10k-50k":
        score += 2;
        break;
      case "50k-100k":
        score += 3;
        break;
      case "100k-500k":
        score += 4;
        break;
      case "500k-1m":
        score += 5;
        break;
      case "1m-10m":
        score += 6;
        break;
      case "10m-50m":
        score += 7;
        break;
      case "50m-100m":
        score += 8;
        break;
      case "100m-500m":
        score += 9;
        break;
      case "500m-1b":
        score += 10;
        break;
      case "over-1b":
        score += 15;
        break;
    }
  }
  if (company?.analytics?.lighthouse) {
    score += 3;
  }
  if (company?.contacts?.phones?.length) {
    score += 2;
  }
  if (company?.contacts?.whatsapps?.length) {
    score += 2;
  }
  if (company?.contacts?.emails?.length) {
    score += 2;
  }
  if (company?.contacts?.lines?.length) {
    score += 2;
  }
  if (company?.urls?.contact) {
    score += 2;
  }
  if ((company?.technologies?.active || []).length > 1) {
    score += 2;
  }
  if ((company?.technologies?.categories || []).length > 1) {
    score += 2;
  }
  if (Object.keys(company?.socials || {}).length) {
    score += Object.keys(company?.socials || {}).length;
  }
  if (company?.vectors?.global) {
    score += 1;
  }
  if (company?.urls?.pricing) {
    score += 1;
  }
  if (company?.urls?.privacy) {
    score += 1;
  }
  if (company?.urls?.terms) {
    score += 1;
  }
  if (company?.urls?.blog) {
    score += 1;
  }
  if (company?.urls?.developers) {
    score += 1;
  }
  if (company?.urls?.docs) {
    score += 1;
  }
  if (company?.urls?.events) {
    score += 1;
  }
  if (company?.urls?.partnership) {
    score += 1;
  }
  if (company?.contents?.podcasts?.apple?.length) {
    score += 2;
  }
  if (company?.contents?.podcasts?.spotify?.length) {
    score += 2;
  }
  if (company?.urls?.sitemap) {
    score += 1;
  }
  if (company?.urls?.status) {
    score += 1;
  }
  if (company?.urls?.updates) {
    score += 1;
  }
  if (company?.urls?.careers) {
    score += 1;
  }
  if (company?.urls?.glassdoor) {
    score += 1;
  }
  return score;
}
function getGeopoint(company) {
  const addressGeopoint = company?.locations?.headquarters?.address?.geopoint;
  if (addressGeopoint && addressGeopoint.lat && addressGeopoint.lon) {
    return company.locations.headquarters.address.geopoint;
  }
  const cityGeopoint = company?.locations?.headquarters?.city?.geopoint;
  if (cityGeopoint && cityGeopoint.lat && cityGeopoint.lon) {
    return company.locations.headquarters.city.geopoint;
  }
  const stateGeopoint = company?.locations?.headquarters?.state?.geopoint;
  if (stateGeopoint && stateGeopoint.lat && stateGeopoint.lon) {
    return company.locations.headquarters.state.geopoint;
  }
  const countyGeopoint = company?.locations?.headquarters?.county?.geopoint;
  if (countyGeopoint && countyGeopoint.lat && countyGeopoint.lon) {
    return company.locations.headquarters.county.geopoint;
  }
  const countryGeopoint = company?.locations?.headquarters?.country?.geopoint;
  if (countryGeopoint && countryGeopoint.lat && countryGeopoint.lon) {
    return company.locations.headquarters.country.geopoint;
  }
  const continentGeopoint = company?.locations?.headquarters?.continent?.geopoint;
  if (continentGeopoint && continentGeopoint.lat && continentGeopoint.lon) {
    return company.locations.headquarters.continent.geopoint;
  }
}
function cleanupCompany(company) {
  company = removeNullUndefinedEmpty(company);
  if (company.companies?.similar) {
    delete company.companies.similar;
  }
  return company;
}
function getVisitableUrls(params) {
  const { company, domain } = params;
  let urls = [];
  if (company?.urls?.blog) {
    urls.push(company.urls.blog);
  }
  if (company?.urls?.developers) {
    urls.push(company.urls.developers);
  }
  if (company?.urls?.events) {
    urls.push(company.urls.events);
  }
  if (company?.urls?.partnership) {
    urls.push(company.urls.partnership);
  }
  if (company?.urls?.sitemap) {
    urls.push(company.urls.sitemap);
  }
  if (company?.urls?.updates) {
    urls.push(company.urls.updates);
  }
  if (company?.urls?.careers) {
    urls.push(company.urls.careers);
  }
  if (company?.urls?.glassdoor) {
    urls.push(company.urls.glassdoor);
  }
  if (company?.urls?.docs) {
    urls.push(company.urls.docs);
  }
  if (company?.urls?.status) {
    urls.push(company.urls.status);
  }
  if (company?.urls?.privacy) {
    urls.push(company.urls.privacy);
  }
  if (company?.urls?.terms) {
    urls.push(company.urls.terms);
  }
  if (company?.urls?.contact) {
    urls.push(company.urls.contact);
  }
  if (company?.socials?.linkedin?.url) {
    urls.push(company.socials.linkedin.url);
  }
  if (company?.socials?.discord?.url) {
    urls.push(company.socials.discord.url);
  }
  if (company?.socials?.dribbble?.url) {
    urls.push(company.socials.dribbble.url);
  }
  if (domain) {
    urls = urls.filter((url) => url.includes(domain));
  }
  return Array.from(new Set(urls));
}

export { castCompany, cleanName, cleanupCompany, companyV2ToElasticSource, elasticResponseToCompanyV2, extractSocialNetworkStats, extractStockExchangeAndSymbol, extractValueAndYear, extractYearFoundedAttributes, formatName, generateNameAlts, getGeopoint, getScore, getVisitableUrls, removeEmptyCategories };
